import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { whenever } from "@vueuse/core";

import { useAuthStore } from "@/stores/Auth";
import { useSectorsStore } from "@/stores/Sectors";
import { ContentfulItem, HasSectorContext, Sector, SectorAlert } from "@/types/entities";

/*********************************************************************/
//                       NON-REACTIVE CONSTANTS                      //
/*********************************************************************/
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

/*********************************************************************/
//                 GLOBALLY SHARED REACTIVE VARIABLES                //
/*********************************************************************/

// A list of product ids for content that is associated with the products in the collection (inclusive)
const includeSectorFilter = ref< Sector[] >( [] );

export function useSectorFilter() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const { allSectors } = storeToRefs( useSectorsStore() );

    whenever( allSectors, () => resetSelectedSectors() );

    const hasSectorsToFilter = computed< boolean >( () => isAuthenticated.value && allSectors.value.length > 0 );

    const sectorIdFilter = computed< number[] >( () => includeSectorFilter.value.map( ( filter: Sector ) => filter.id ) );

    function filterOutContentItemsThatDoNotContainSelectedSectors( contentItems: HasSectorContext[] ): ContentfulItem[] {
        logger.debug( '[ sector-filter ].filterOutItemsThatDoNotContainSelectedSectors -> sectorIdFilter : ', sectorIdFilter );
        return contentItems.filter( ( item: HasSectorContext ) => item.sectors.some( ( sector: Sector ) => sectorIdFilter.value.includes( sector.id ) ) );
    }

    function filterOutItemsThatDoNotContainSelectedSectors( alert: SectorAlert ): boolean {
        logger.debug( '[ sector-filter ].filterOutItemsThatDoNotContainSelectedSectors -> sectorIdFilter : ', sectorIdFilter );
        return sectorIdFilter.value.includes( alert.sector_id );
    }

    function resetSelectedSectors(): void {
        includeSectorFilter.value = [ ...allSectors.value ];
    }

    function disableClearFilter(): boolean {
        return includeSectorFilter.value.length === 0;
    }

    // Initialize the selection of sectors to include all
    resetSelectedSectors();

    return {
        disableClearFilter,
        filterOutContentItemsThatDoNotContainSelectedSectors,
        filterOutItemsThatDoNotContainSelectedSectors,
        hasSectorsToFilter,
        includeSectorFilter,
        resetSelectedSectors
    }
}
